import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Alert, Chip } from '@mui/material'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { getIngestStatus, triggerIngest } from '../../../../../services/analytics/analytics.service'
import { useSelector } from 'react-redux'

const isMaestroRole = (roles = []) => roles.some((r) => r?.id === 'Maestro')

// MF-555 — "in progress" feedback: three dots that fade in and out one after another.
const AnimatedDots = () => (
  <Box
    component='span'
    aria-hidden
    sx={{
      display: 'inline-flex',
      marginLeft: '1px',
      '& span': { animation: 'analyticsDots 1.4s infinite', opacity: 0.2 },
      '& span:nth-of-type(2)': { animationDelay: '0.2s' },
      '& span:nth-of-type(3)': { animationDelay: '0.4s' },
      '@keyframes analyticsDots': {
        '0%, 80%, 100%': { opacity: 0.2 },
        '40%': { opacity: 1 }
      }
    }}
  >
    <span>.</span>
    <span>.</span>
    <span>.</span>
  </Box>
)

// Soft pulse for the status chip while a refresh is queued or running.
const pulseSx = {
  animation: 'analyticsPulse 1.6s ease-in-out infinite',
  '@keyframes analyticsPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 }
  }
}

const AnalyticsDataRefresh = () => {
  const userRoles = useSelector((state) => state?.session?.user?.roles || [])
  const isMaestro = isMaestroRole(userRoles)

  const [status, setStatus] = useState(null)
  const [triggering, setTriggering] = useState(false)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getIngestStatus()
      // /ingest/status devuelve el objeto SIN envoltura `data` ({ running, lastCompleted, ... }).
      setStatus(res?.data ?? res ?? null)
    } catch {
      // analytics service might not be available
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  useEffect(() => {
    if (status?.running) {
      pollRef.current = setInterval(fetchStatus, 5000)
    } else {
      clearInterval(pollRef.current)
    }
    return () => clearInterval(pollRef.current)
  }, [status?.running, fetchStatus])

  const onRefresh = async () => {
    setTriggering(true)
    setError(null)
    try {
      // MF-541 — refresh ONLY this school (per-tenant). Running all schools is done via the
      // overnight cron / one-by-one, not from this button.
      await triggerIngest({ scope: 'tenant' })
      await fetchStatus()
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Error al iniciar la actualización')
    } finally {
      setTriggering(false)
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }

  return (
    <>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          Analytics Data Refresh
        </Typography>
      </Box>
      <Box className='px-4 pt-6 pb-4 flex flex-col gap-4 max-w-lg'>
        <div className='grid grid-cols-4 items-center gap-4'>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Status
          </Typography>
          <div className='col-span-3'>
            {status?.running ? (
              // MF-555 — the central server queues refreshes: "Queued" until the worker picks it up.
              <Chip
                label={
                  <span>
                    {status?.queued && !status?.startedAt ? 'Queued' : 'Updating'}
                    <AnimatedDots />
                  </span>
                }
                color='warning'
                size='small'
                sx={pulseSx}
                role='status'
                aria-live='polite'
              />
            ) : status?.incomplete ? (
              <Chip label='Partial data' color='warning' variant='outlined' size='small' />
            ) : status?.lastCompleted ? (
              <Chip label='Up to date' color='success' size='small' />
            ) : (
              <Chip label='No data yet' color='default' size='small' />
            )}
          </div>

          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Last update
          </Typography>
          <Typography className='col-span-3' variant='body2'>
            {formatDate(status?.lastCompleted)}
          </Typography>

          {status?.lastError && (
            <>
              <Typography variant='inherit' color='error' className='font-bold' align='right' component='p'>
                Last error
              </Typography>
              <Typography className='col-span-3' variant='body2' color='error'>
                {status.lastError}
              </Typography>
            </>
          )}
        </div>

        {isMaestro && (
          <Box className='flex flex-col gap-2 mt-2'>
            <ButtonStartIcon
              onClick={onRefresh}
              color='primary'
              variant='contained'
              disabled={status?.running || triggering}
              loading={triggering || status?.running}
              startIcon={['fas', 'sync']}
            >
              {status?.running ? (
                <span>
                  {status?.queued && !status?.startedAt ? 'Queued' : 'Updating'}
                  <AnimatedDots />
                </span>
              ) : (
                'Refresh now'
              )}
            </ButtonStartIcon>
            <Typography variant='caption' color='text.secondary'>
              This refreshes analytics data for this school only.
              {status?.running &&
                (status?.queued && !status?.startedAt
                  ? ' Waiting for its turn — schools are refreshed one at a time.'
                  : ' Already running — check back in a few minutes.')}
              {!status?.running &&
                status?.incomplete &&
                ' The last refresh could not read every data source; the previous data was kept for those.'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity='error' onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>
    </>
  )
}

export default AnalyticsDataRefresh
